import cn from 'classnames'
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { useIsomorphicLayoutEffect } from 'react-use'

import styles from './EditableText.module.scss'

import Text from '~lib/ui-kit/Text'

export interface EditableTextProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  active?: boolean
  as?: React.ElementType
  bold?: boolean
  defaultValue?: string
  disabled?: boolean
  error?: string
  type?: 'heading-l' | 'heading-m' | 'heading-s' | 'body-m' | 'body-s' | 'caption'
  onChangeActive?: (active: boolean) => void
  value?: string
  minRows?: number
  maxRows?: number
  maxLength?: number
}

function EditableText(
  {
    as: Element = 'p',
    active,
    bold,
    type = 'body-m',
    error,
    className,
    disabled,
    placeholder,
    rows = 1,
    minRows = 1,
    maxRows = 1,
    maxLength,
    readOnly,
    style,
    value,
    defaultValue,
    onChange,
    onChangeActive,
    onFocus,
    onBlur,
    ...props
  }: EditableTextProps,
  ref
) {
  const [activeState, setActiveState] = useState(active)
  const [innerValue, setInnerValue] = useState(() => value || defaultValue)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const pRef = useRef<HTMLElement>(null)

  useImperativeHandle(ref, () => pRef.current)

  useEffect(() => {
    if (typeof active !== 'undefined') {
      setActiveState(active)
    }
  }, [active])

  const changeActive = (value: boolean) => {
    if (onChangeActive) {
      onChangeActive(value)
    } else {
      setActiveState(value)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    e.persist()
    const value = e.currentTarget.value.replace(/\n/g, '')
    e.currentTarget.value = value
    setInnerValue(value)
    onChange?.(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    e.persist()
    changeActive(false)
    if (onBlur) onBlur(e)
  }

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // console.log('focus')
    e.persist()
    changeActive(true)
    if (onFocus) onFocus(e)
  }

  const handleClickText = () => {
    if (!disabled && !readOnly) {
      changeActive(true)
    }
  }

  useEffect(() => {
    if (typeof value !== 'undefined') {
      setInnerValue(value)
    }
  }, [value])

  useIsomorphicLayoutEffect(() => {
    if (activeState) {
      const len = textareaRef.current?.value.length
      textareaRef.current?.focus()
      textareaRef.current?.setSelectionRange(len, len)
    }
  }, [activeState])

  return (
    <>
      <TextareaAutosize
        maxLength={maxLength}
        ref={textareaRef}
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        spellCheck={false}
        className={cn(
          styles.root,
          styles[type],
          bold && styles.bold,
          disabled && styles.disabled,
          readOnly && styles.readOnly,
          !activeState && styles.hidden,
          className
        )}
        onChange={handleChange}
        onBlur={handleBlur}
        value={innerValue}
        {...props}
      />

      {!activeState && (
        <Element
          ref={pRef}
          // tabIndex is required here because today's browsers define focus() on HTMLElement, but an element won't actually take focus unless it's one of:
          // * HTMLAnchorElement/HTMLAreaElement with an href
          // * HTMLInputElement/HTMLSelectElement/HTMLTextAreaElement/HTMLButtonElement but not with disabled (IE actually gives you an error if you try), and file uploads have unusual behaviour for security reasons
          // * HTMLIFrameElement (though focusing it doesn't do anything useful). Other embedding elements also, maybe, I haven't tested them all.
          // * Any element with a tabindex
          tabIndex={props.tabIndex || 0}
          onMouseUp={handleClickText}
          className={cn(
            styles.root,
            styles[type],
            bold && styles.bold,
            disabled && styles.disabled,
            readOnly && styles.readOnly,
            !innerValue && styles.placeholder,
            className
          )}
          onFocus={handleFocus}
        >
          {innerValue || placeholder}
        </Element>
      )}

      {error && (
        <Text as="div" type="caption" variant="danger" className={styles.error}>
          {error}
        </Text>
      )}
    </>
  )
}

EditableText.displayName = 'EditableText'

export default forwardRef(EditableText)
